import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowLeft, MapPin, Calendar, Clock, DollarSign, User, Loader, AlertCircle } from 'lucide-react';
import Navigation from './Navigation';
import Footer from './Footer';

const API_BASE = 'https://hive-backend-4gcf.onrender.com';

function authHeaders() {
  const token = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

function getUrgencyColor(urgency) {
  switch (urgency) {
    case 'asap': return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
    case 'urgent': return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400';
    default: return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
  }
}

export default function JobDetailsPage() {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const [applyError, setApplyError] = useState('');

  const isLoggedIn = !!localStorage.getItem('token');

  useEffect(() => {
    fetchJob();
  }, [id]);

  const fetchJob = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/jobs/${id}`, { headers: authHeaders() });
      const data = await res.json();
      if (res.ok) {
        setJob(data.job || data);
      } else {
        setError('Job not found.');
      }
    } catch (err) {
      setError('Could not load job details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    if (!isLoggedIn) {
      window.location.href = '/auth';
      return;
    }
    setApplying(true);
    setApplyError('');
    try {
      const res = await fetch(`${API_BASE}/api/jobs/${id}/apply`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (res.ok) {
        setApplied(true);
      } else {
        const data = await res.json();
        setApplyError(data.message || 'Failed to apply.');
      }
    } catch (err) {
      setApplyError('Could not connect. Please try again.');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 transition-colors duration-300">
      <Navigation />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-20">
        <button onClick={() => window.history.back()}
          className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-yellow-600 dark:hover:text-yellow-400 transition-colors mb-6">
          <ArrowLeft className="w-5 h-5" /> Back
        </button>

        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-2xl text-red-600 dark:text-red-400">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p>{error}</p>
          </div>
        )}

        {job && (
          <div className="space-y-6">
            {/* Header card */}
            <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-3xl shadow-xl border border-gray-200/50 dark:border-gray-700/50 p-8">
              <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-black text-gray-900 dark:text-white mb-2">{job.title}</h1>
                  <div className="flex flex-wrap gap-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${getUrgencyColor(job.urgency)}`}>
                      {job.urgency === 'asap' ? '🔴 ASAP' : job.urgency === 'urgent' ? '🟠 Urgent' : '🟢 Normal'}
                    </span>
                    {job.category && (
                      <span className="px-3 py-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-full text-xs font-semibold">
                        {job.category}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Budget</p>
                  <p className="text-2xl font-black text-green-600 dark:text-green-400">
                    {job.budget ? `LKR ${job.budget.toLocaleString()}` : 'Open'}
                  </p>
                </div>
              </div>

              {/* Details */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
                {job.location && (
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <MapPin className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                    <span>{job.location}</span>
                  </div>
                )}
                {job.preferredDate && (
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <Calendar className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                    <span>{new Date(job.preferredDate).toLocaleDateString()}</span>
                  </div>
                )}
                {job.preferredTime && (
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <Clock className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                    <span>{job.preferredTime}</span>
                  </div>
                )}
                {job.applications?.length !== undefined && (
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <User className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                    <span>{job.applications.length} applicant{job.applications.length !== 1 ? 's' : ''}</span>
                  </div>
                )}
              </div>

              {/* Description */}
              {job.description && (
                <div>
                  <h3 className="font-bold text-gray-900 dark:text-white mb-2">Description</h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4">
                    {job.description}
                  </p>
                </div>
              )}
            </div>

            {/* Posted by */}
            {job.customerId && (
              <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-3xl shadow-xl border border-gray-200/50 dark:border-gray-700/50 p-6">
                <h3 className="font-bold text-gray-900 dark:text-white mb-4">Posted By</h3>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-2xl flex items-center justify-center text-white font-black text-lg">
                    {job.customerId.name?.charAt(0) || 'U'}
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 dark:text-white">{job.customerId.name || 'Customer'}</p>
                    {job.customerId.location && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />{job.customerId.location}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Apply CTA */}
            <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-3xl shadow-xl border border-gray-200/50 dark:border-gray-700/50 p-6">
              {applied ? (
                <div className="text-center py-4">
                  <div className="text-4xl mb-3">✅</div>
                  <h3 className="text-xl font-black text-gray-900 dark:text-white mb-2">Application Sent!</h3>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">The customer will be in touch if they'd like to proceed.</p>
                </div>
              ) : (
                <div>
                  <h3 className="font-bold text-gray-900 dark:text-white mb-2">Interested in this job?</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                    {isLoggedIn
                      ? "Let the customer know you're available and can help with this job."
                      : 'Sign in to apply for this job.'}
                  </p>
                  {applyError && (
                    <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-xl text-red-600 dark:text-red-400 text-sm">
                      {applyError}
                    </div>
                  )}
                  <button
                    onClick={handleApply}
                    disabled={applying}
                    className="w-full py-4 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white font-black rounded-2xl hover:shadow-xl hover:shadow-yellow-400/30 transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-60 flex items-center justify-center gap-2 text-lg"
                  >
                    {applying && <Loader className="w-5 h-5 animate-spin" />}
                    {applying ? 'Applying...' : isLoggedIn ? 'Apply for This Job' : 'Sign In to Apply'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}